import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import config from './config';
import authConfig from './authConfig';

interface UserProfile {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const NAME_MAX_LENGTH = 100;
const MOBILE_MAX_LENGTH = 20;
const ADDRESS_MAX_LENGTH = 200;
const CITY_STATE_COUNTRY_MAX_LENGTH = 100;
const ZIP_MAX_LENGTH = 20;
const MOBILE_PATTERN = /^[0-9+()\-\s]{8,20}$/;
const COUNTRY_OPTIONS = [
  'Australia', 'Bangladesh', 'Canada', 'India', 'Malaysia', 'New Zealand', 'Pakistan', 'Singapore', 'United Arab Emirates', 'United Kingdom', 'United States', 'Afghanistan', 'Argentina', 'Belgium', 'Brazil', 'China', 'France', 'Germany', 'Indonesia', 'Ireland', 'Japan', 'Kenya', 'Nepal', 'Nigeria', 'Philippines', 'South Africa', 'Sri Lanka', 'Thailand', 'Vietnam', 'Other'
];

const getMobileValidationMessage = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (!MOBILE_PATTERN.test(trimmed)) {
    return 'Enter a valid mobile number (8-20 chars, digits and + - ( ) allowed).';
  }

  return '';
};

const MyProfile: React.FC = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [profile, setProfile] = useState<UserProfile>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [mobileError, setMobileError] = useState('');

  const getToken = useCallback(async () => {
    try {
      const tokenResponse: any = await getAccessTokenSilently({
        authorizationParams: {
          audience: authConfig.audience,
        },
      } as any);
      return typeof tokenResponse === 'string' ? tokenResponse : tokenResponse?.access_token ?? '';
    } catch {
      const tokenResponse: any = await getAccessTokenSilently({
        authorizationParams: {
          audience: authConfig.audience,
        },
        cacheMode: 'off',
      } as any);
      return typeof tokenResponse === 'string' ? tokenResponse : tokenResponse?.access_token ?? '';
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated || !user?.sub) {
        return;
      }

      setIsFetching(true);
      try {
        const token = await getToken();
        const response = await fetch(`${config.apiBaseUrl}/api/profile/${encodeURIComponent(user.sub)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProfile({
            firstName: data.firstName || user.given_name || '',
            lastName: data.lastName || user.family_name || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            zipCode: data.zipCode || '',
            country: data.country || '',
          });
          setMobileError('');
          return;
        }

        setProfile({
          firstName: user.given_name || '',
          lastName: user.family_name || '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        });
        setMobileError('');
      } catch (error) {
        console.error('Error loading profile:', error);
        setProfile({
          firstName: user.given_name || '',
          lastName: user.family_name || '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        });
        setMobileError('');
      } finally {
        setIsFetching(false);
      }
    };

    loadProfile();
  }, [isAuthenticated, user, getToken]);

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    if (field === 'firstName' || field === 'lastName') {
      const bounded = value.slice(0, NAME_MAX_LENGTH);
      setProfile(prev => ({
        ...prev,
        [field]: bounded
      }));
      return;
    }

    if (field === 'phone') {
      const bounded = value.slice(0, MOBILE_MAX_LENGTH);
      setProfile(prev => ({
        ...prev,
        [field]: bounded
      }));
      setMobileError(getMobileValidationMessage(bounded));
      return;
    }

    if (field === 'address') {
      const bounded = value.slice(0, ADDRESS_MAX_LENGTH);
      setProfile(prev => ({
        ...prev,
        [field]: bounded
      }));
      return;
    }

    if (field === 'city' || field === 'state' || field === 'country') {
      const bounded = value.slice(0, CITY_STATE_COUNTRY_MAX_LENGTH);
      setProfile(prev => ({
        ...prev,
        [field]: bounded
      }));
      return;
    }

    if (field === 'zipCode') {
      const bounded = value.slice(0, ZIP_MAX_LENGTH);
      setProfile(prev => ({
        ...prev,
        [field]: bounded
      }));
      return;
    }

    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProfile = async () => {
    if (!isAuthenticated || !user?.sub) return;

    const requiredFields: Array<{ key: keyof UserProfile; label: string }> = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'phone', label: 'Mobile Number' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zipCode', label: 'Zip Code' },
      { key: 'country', label: 'Country' },
    ];

    const missingField = requiredFields.find(({ key }) => !String(profile[key] ?? '').trim());
    if (missingField) {
      setFormError(`${missingField.label} is required.`);
      setSaveMessage('Please complete all required fields before saving.');
      return;
    }

    const mobileValidation = getMobileValidationMessage(profile.phone);
    if (mobileValidation) {
      setMobileError(mobileValidation);
      setFormError(mobileValidation);
      setSaveMessage('Please fix validation errors before saving.');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');
    setFormError('');

    try {
      const token = await getToken();
      const response = await fetch(`${config.apiBaseUrl}/api/profile/${encodeURIComponent(user.sub)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          zipCode: profile.zipCode,
          country: profile.country,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save profile');
      }

      setSaveMessage('Profile saved successfully!');
      setIsEditing(false);
      setFormError('');
      setMobileError('');
      setTimeout(() => {
        window.location.assign('/analytics');
      }, 250);
    } catch (error) {
      setSaveMessage('Error saving profile. Please try again.');
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSaveMessage('');
    setFormError('');
    setMobileError('');
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        height: '400px',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        Loading...
      </div>
    );
  }

  if (isFetching) {
    return (
      <div style={{ 
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        height: '400px',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        Loading profile...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        textAlign: 'left', 
        padding: '60px 20px',
        color: '#666',
        fontSize: '1.1rem'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔒</div>
        <div>Please log in to access your profile.</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '40px 20px', 
      maxWidth: '800px', 
      margin: '0 auto', 
      fontFamily: 'Arial, sans-serif',
      textAlign: 'left'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: window.innerWidth < 768 ? '24px' : '40px',
        flexWrap: window.innerWidth < 480 ? 'wrap' : 'nowrap',
        gap: window.innerWidth < 480 ? '16px' : '0'
      }}>
        <h1 style={{ 
          textAlign: 'left', 
          color: '#333',
          fontSize: window.innerWidth < 480 ? '1.5rem' : window.innerWidth < 768 ? '2rem' : '2.5rem',
          fontWeight: 'bold',
          margin: 0
        }}>
          My Profile
        </h1>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            style={{
              padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
              backgroundColor: '#2c5aa0',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap'
            }}
          >
            Edit Profile
          </button>
        ) : (
          <div style={{ display: 'flex', gap: window.innerWidth < 480 ? '8px' : '12px' }}>
            <button
              onClick={saveProfile}
              disabled={isSaving}
              style={{
                padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
                backgroundColor: isSaving ? '#ccc' : '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelEdit}
              style={{
                padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: '#fff7ed',
          border: '1px solid #fdba74',
          color: '#9a4d00',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>
          Please complete your profile information to access other pages in the app.
        </div>

        {/* Profile Picture and Basic Info */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          marginBottom: '40px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#2c5aa0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '2rem',
            fontWeight: 'bold',
            marginRight: '24px',
            backgroundImage: user?.picture ? `url(${user.picture})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>
            {!user?.picture && (user?.name?.charAt(0) || user?.email?.charAt(0) || 'U')}
          </div>
          <div>
            <h2 style={{ 
              color: '#2c5aa0', 
              margin: '0 0 8px 0', 
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {user?.name || `${profile.firstName} ${profile.lastName}`.trim() || 'User'}
            </h2>
            <div style={{ color: '#666', fontSize: '1rem' }}>
              {user?.email}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '4px' }}>
              Member since {new Date(user?.updated_at || Date.now()).toLocaleDateString()}
            </div>
          </div>
        </div>

        {saveMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: saveMessage.includes('Error') || formError ? '#fef2f2' : '#f0fdf4',
            color: saveMessage.includes('Error') || formError ? '#dc3545' : '#28a745',
            border: `1px solid ${saveMessage.includes('Error') || formError ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            {saveMessage || formError}
          </div>
        )}

        {/* Personal Information */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Personal Information
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                First Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  maxLength={NAME_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.firstName ? '#333' : '#999'
                }}>
                  {profile.firstName || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Last Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  maxLength={NAME_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.lastName ? '#333' : '#999'
                }}>
                  {profile.lastName || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Mobile Number <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    maxLength={MOBILE_MAX_LENGTH}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '1rem',
                      border: `2px solid ${mobileError ? '#dc3545' : '#e0e0e0'}`,
                      borderRadius: '8px',
                      backgroundColor: '#fff'
                    }}
                  />
                  {mobileError && (
                    <div style={{ marginTop: '6px', color: '#dc3545', fontSize: '0.85rem' }}>
                      {mobileError}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.phone ? '#333' : '#999'
                }}>
                  {profile.phone || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Address <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  maxLength={ADDRESS_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.address ? '#333' : '#999'
                }}>
                  {profile.address || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                City <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  maxLength={CITY_STATE_COUNTRY_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.city ? '#333' : '#999'
                }}>
                  {profile.city || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                State <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  maxLength={CITY_STATE_COUNTRY_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.state ? '#333' : '#999'
                }}>
                  {profile.state || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Zip Code <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  maxLength={ZIP_MAX_LENGTH}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.zipCode ? '#333' : '#999'
                }}>
                  {profile.zipCode || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Country <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {isEditing ? (
                <select
                  value={profile.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="">Select country</option>
                  {COUNTRY_OPTIONS.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.country ? '#333' : '#999'
                }}>
                  {profile.country || 'Not provided'}
                </div>
              )}
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default MyProfile;